import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { THEMES, ThemeName } from '../../constants/themes';
import { environment } from '../../../environments/environment';

export interface ThemeState {
  currentTheme: ThemeName;
  isDarkMode: boolean;
  isInitialized: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeSubject = new BehaviorSubject<ThemeState>({
    currentTheme: 'light',
    isDarkMode: false,
    isInitialized: false
  });

  public theme$ = this.themeSubject.asObservable();
  private readonly THEME_STORAGE_KEY = `${environment.storagePrefix || ''}user_theme`;

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedTheme = this.getStoredTheme();
    if (savedTheme) {
      this.applyTheme(savedTheme, false, false);
    } else {
      this.applyTheme('light', false, false);
    }
  }

  applyTheme(themeName: ThemeName, saveToStorage: boolean = true, _disableTransitions: boolean = true): void {
    const themeStyle = THEMES[themeName];
    if (!themeStyle) {
      themeName = 'light';
    } else {
      Object.entries(THEMES[themeName]).forEach(([property, value]) => {
        document.documentElement.style.setProperty(property, value);
      });
    }

    const isDarkMode = themeName !== 'light';
    this.themeSubject.next({
      currentTheme: themeName,
      isDarkMode,
      isInitialized: true
    });

    if (saveToStorage) {
      this.storeTheme(themeName);
    }
  }

  getCurrentTheme(): ThemeName {
    return this.themeSubject.value.currentTheme;
  }

  isDarkMode(): boolean {
    return this.themeSubject.value.isDarkMode;
  }

  isInitialized(): boolean {
    return this.themeSubject.value.isInitialized;
  }

  private storeTheme(themeName: ThemeName): void {
    try {
      localStorage.setItem(this.THEME_STORAGE_KEY, themeName);
    } catch (_e) {
      // ignore
    }
  }

  private getStoredTheme(): ThemeName | null {
    try {
      const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
      return stored as ThemeName || null;
    } catch (_e) {
      return null;
    }
  }

  getCurrentThemeStyle(): { [key: string]: string } {
    return THEMES[this.getCurrentTheme()] || THEMES['light'];
  }
}
